import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

const FullscreenSlider = () => {
  const { t } = useTranslation();
  const images = t("slides", { returnObjects: true });

  const [current, setCurrent] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const theme = useTheme();

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Detect if current language is RTL
  const [isRTL, setIsRTL] = useState(false);
  useEffect(() => {
    const rtlLanguages = ["ar", "he", "fa", "ur"];
    setIsRTL(rtlLanguages.includes(i18next.language));
  }, [i18next.language]);

  // Optional: set dir attribute globally for the entire document
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const prevSlide = () => {
    setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setPanelOpen(false);
  };

  const nextSlide = () => {
    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setPanelOpen(false);
  };

  const goToSlide = (index) => {
    setCurrent(index);
    setPanelOpen(false);
  };

  const handleLearnMore = () => {
    setPanelOpen(true);
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
  };

  return (
    <Box sx={{ p: isSmall ? 1 : 2, mt: "30px" }}>
      <Paper
        elevation={4}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 1600,
          height: isSmall ? "50vh" : isMedium ? "60vh" : "70vh",
          margin: "0 auto",
          overflow: "hidden",
          borderRadius: 5,
          borderTop: `5px solid #CE8238`,
          borderLeft: `5px solid #0057A4`,
          borderBottom: `5px solid #CE8238`,
          borderRight: `5px solid #0057A4`,
          // REMOVE direction here to keep layout LTR always
        }}
      >
        {/* Slide container */}
        <Box
          sx={{
            display: "flex",
            width: `${images.length * 100}%`,
            transform: `translateX(-${current * (100 / images.length)}%)`,
            transition: "transform 0.8s ease-in-out",
            height: "100%",
            // Keep LTR layout for design consistency
            direction: "ltr",
          }}
        >
          {images.map((img, index) => (
            <Box
              key={index}
              sx={{
                width: `${100 / images.length}%`,
                backgroundImage: `url(${img.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 20,
                  left: 20, // fixed position left for design
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  padding: isSmall ? "8px 12px" : "12px 20px",
                  borderRadius: "8px",
                  maxWidth: isSmall ? "90%" : "40%",
                  zIndex: 5,
                  textAlign: isRTL ? "right" : "left",
                  direction: isRTL ? "rtl" : "ltr", // only on text container
                }}
              >
                <Box
                  sx={{
                    fontSize: isSmall ? 18 : isMedium ? 24 : 28,
                    fontWeight: "bold",
                    mb: 1,
                  }}
                >
                  {img.name}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleLearnMore}
                  sx={{
                    bgcolor: "#0057A4",
                    color: "white",
                    ":hover": { bgcolor: "#eee" },
                    fontSize: isSmall ? 12 : 14,
                    py: isSmall ? 0.5 : 1,
                    px: isSmall ? 1 : 2,
                    textTransform: "none",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  {t("ReadMore")}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Arrows */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: "absolute",
            top: "50%",
            left: 10,
            transform: "translateY(-50%)",
            bgcolor: "rgba(255,255,255,0.8)",
            ":hover": { bgcolor: "#fff" },
            zIndex: 10,
            width: isSmall ? 30 : 40,
            height: isSmall ? 30 : 40,
          }}
          aria-label={t("Previous slide")}
        >
          <ArrowBackIos sx={{ fontSize: isSmall ? 16 : 24 }} />
        </IconButton>
        <IconButton
          onClick={nextSlide}
          sx={{
            position: "absolute",
            top: "50%",
            right: 10,
            transform: "translateY(-50%)",
            bgcolor: "rgba(255,255,255,0.8)",
            ":hover": { bgcolor: "#fff" },
            zIndex: 10,
            width: isSmall ? 30 : 40,
            height: isSmall ? 30 : 40,
          }}
          aria-label={t("Next slide")}
        >
          <ArrowForwardIos sx={{ fontSize: isSmall ? 16 : 24 }} />
        </IconButton>

        {/* Thumbnails */}
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.4)",
            padding: "6px 10px",
            borderRadius: "12px",
            overflowX: "auto",
            maxWidth: "90%",
            direction: "ltr", // keep layout LTR so thumbnails always left-to-right
          }}
        >
          {images.map((img, index) => (
            <Box
              key={index}
              onClick={() => goToSlide(index)}
              sx={{
                flex: "0 0 auto",
                width: isSmall ? 40 : 80,
                height: isSmall ? 24 : 60,
                backgroundImage: `url(${img.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border:
                  current === index
                    ? "2px solid white"
                    : "5px solid transparent",
                borderRadius: "4px",
                cursor: "pointer",
                opacity: current === index ? 1 : 0.6,
                transition: "opacity 0.3s",
              }}
              aria-label={t("Go to slide {{index}}", { index: index + 1 })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  goToSlide(index);
                }
              }}
            />
          ))}
        </Box>

        {/* Overlay for mobile when panel open */}
        {isSmall && panelOpen && (
          <Box
            onClick={handleClosePanel}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "rgba(0,0,0,0.5)",
              zIndex: 15,
            }}
            aria-hidden="true"
          />
        )}

        {/* Internal Sliding Panel */}
        <Box
          sx={{
            position: isSmall ? "fixed" : "absolute",
            top: 0,
            right: 0, // always slide from right side (no flip)
            bottom: 0,
            width: isSmall ? "85vw" : 350,
            height: isSmall ? "100vh" : "100%",
            bgcolor: "rgba(255,255,255,0.97)",
            boxShadow: "-3px 0 10px rgba(0,0,0,0.3)",
            borderTopLeftRadius: isSmall ? 16 : 0,
            borderBottomLeftRadius: isSmall ? 16 : 0,
            transform: panelOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.5s ease-in-out",
            p: 3,
            zIndex: 9999,
            overflowY: "auto",
            pointerEvents: panelOpen ? "auto" : "none",
            direction: "ltr", // keep container LTR for design consistency
            textAlign: isRTL ? "right" : "left", // align text inside
          }}
          aria-hidden={!panelOpen}
          aria-label={t("Details about {{name}}", { name: images[current].name })}
          role="region"
        >
          <Button
            onClick={handleClosePanel}
            variant="text"
            sx={{
              mb: 2,
              color: "primary.main",
              fontWeight: "bold",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
              textTransform: "none",
            }}
            aria-label={t("Close details panel")}
          >
            {t("Close")}
          </Button>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ direction: isRTL ? "rtl" : "ltr", textAlign: isRTL ? "right" : "left" }}
          >
            {images[current].name}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-line",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {images[current].description}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default FullscreenSlider;
